import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Table, Row, Col, Card, CardBody, Input, Badge,
    Pagination, PaginationItem, PaginationLink, Label, Form, Progress
} from "reactstrap";
import { FiTrendingUp, FiDollarSign, FiPercent, FiRefreshCcw, FiFileText } from "react-icons/fi";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Cell, Legend
} from "recharts";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";

// MUI Imports for Premium DatePicker
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const InvoiceReportList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [rawResponse, setRawResponse] = useState(null);

    const [filters, setFilters] = useState({
        search: "",
        from_date: dayjs(),
        to_date: dayjs(),
        page: 1,
        limit: 10,
        sort: "DESC",
        filter_type: { value: "today", label: "Today" }
    });

    const filterOptions = [
        { value: "today", label: "Today" },
        { value: "week", label: "Last Week" },
        { value: "month", label: "Last Month" },
        { value: "3months", label: "Last 3 Months" },
        { value: "custom", label: "Custom Range" }
    ];

    const loadReports = async (currentFilters = filters) => {
        dispatch(loadingTrue());
        try {
            const queryParams = new URLSearchParams({
                page: currentFilters.page,
                limit: currentFilters.limit,
                sort: currentFilters.sort,
                filter: currentFilters.filter_type.value,
                search: currentFilters.search.length >= 2 ? currentFilters.search : "",
                from_date: currentFilters.from_date ? currentFilters.from_date.format('YYYY-MM-DD') : "",
                to_date: currentFilters.to_date ? currentFilters.to_date.format('YYYY-MM-DD') : ""
            }).toString();

            const res = await Handler({ method: "GET", url: `/report/invoice_report.php?${queryParams}` });
            if (res?.success) {
                setRawResponse(res);
            }
        } catch (err) {
            toast.error("Sync Failed");
        } finally {
            dispatch(loadingFalse());
        }
    };

    useEffect(() => {
        if (filters.filter_type.value !== 'custom') {
            loadReports();
        }
    }, [filters.page, filters.sort]);

    const handlePeriodChange = (selectedOption) => {
        const type = selectedOption.value;
        let from = dayjs();
        let to = dayjs();

        if (type === 'week') from = dayjs().subtract(7, 'day');
        else if (type === 'month') from = dayjs().subtract(1, 'month');
        else if (type === '3months') from = dayjs().subtract(3, 'month');

        const newFilters = {
            ...filters,
            filter_type: selectedOption,
            from_date: from,
            to_date: to,
            page: 1
        };

        setFilters(newFilters);
        if (type !== 'custom') {
            loadReports(newFilters);
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setFilters({ ...filters, page: 1 });
        loadReports();
    };

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            minHeight: "45px",
            boxShadow: "none",
            '&:hover': { border: '1px solid #f16436' }
        })
    };

    // 🔥 Chart Transformation using Invoice Number
    const chartData = useMemo(() => {
        const items = rawResponse?.data || [];
        return items.map(item => ({
            name: item?.invoice_no || 'INV', // 🔥 Chart X-axis-la ippo exact Invoice Number varum
            net: item?.financials?.net_amount || 0,
            paid: item?.financials?.paid || 0,
        })).reverse();
    }, [rawResponse]);

    const reports = rawResponse?.data || [];
    const summary = rawResponse?.summary || {};
    const pagination = rawResponse?.pagination || {};

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 bg-light min-vh-100">
                <div className="d-md-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-bold m-0 text-dark">Revenue Analytics</h3>
                        <p className="text-muted small m-0">Invoices, Adjustments & Collection Tracking</p>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                        <div style={{ width: '180px' }}>
                            <Select
                                options={filterOptions}
                                value={filters.filter_type}
                                onChange={handlePeriodChange}
                                styles={customSelectStyles}
                            />
                        </div>
                        <Button color="white" className="shadow-sm border rounded-pill px-3 py-2" onClick={() => loadReports()}>
                            <FiRefreshCcw />
                        </Button>
                        <Button color="primary" className="px-4 shadow-sm fw-bold rounded-pill" onClick={() => navigate("/invoice/add")}>
                            New Invoice
                        </Button>
                    </div>
                </div>

                <Row className="g-3 mb-4">
                    <StatCard
                        title="Total Invoice Value"
                        value={`₹${summary?.total_invoice_amount?.toLocaleString() || 0}`}
                        icon={<FiDollarSign />}
                        color="#0d6efd" bg="#0d6efd15"
                    />
                    <StatCard
                        title="Realization Rate"
                        value={reports.length > 0 ? `${(reports.reduce((a, b) => a + (b.financials.paid_percentage || 0), 0) / reports.length).toFixed(1)}%` : '0%'}
                        icon={<FiPercent />}
                        color="#198754" bg="#19875415"
                    />
                    <StatCard
                        title="Active Invoices"
                        value={pagination?.total_records || 0}
                        icon={<FiFileText />}
                        color="#6f42c1" bg="#6f42c115"
                    />
                </Row>

                {filters.filter_type.value === 'custom' && (
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <CardBody className="p-4">
                            <Row className="g-3 align-items-end">
                                <Col md={4}>
                                    <Label className="x-small fw-bold text-muted d-block mb-2 text-uppercase">From Date</Label>
                                    <DatePicker
                                        disableFuture
                                        format="DD/MM/YYYY"
                                        value={filters.from_date}
                                        maxDate={dayjs()}
                                        onChange={(val) => setFilters({ ...filters, from_date: val })}
                                        sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff' } }}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label className="x-small fw-bold text-muted d-block mb-2 text-uppercase">To Date</Label>
                                    <DatePicker
                                        disableFuture
                                        format="DD/MM/YYYY"
                                        value={filters.to_date}
                                        minDate={filters.from_date}
                                        maxDate={dayjs()}
                                        onChange={(val) => setFilters({ ...filters, to_date: val })}
                                        sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff' } }}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Button color="primary" block className="fw-bold rounded-3 shadow-sm" style={{ height: '54px' }} onClick={() => loadReports()}>
                                        Sync Analytics
                                    </Button>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                )}

                <Card className="border-0 shadow-sm rounded-4 mb-4">
                    <CardBody className="p-4">
                        <h6 className="fw-bold mb-4 text-uppercase small text-muted">Realization vs Net Amount</h6>
                        <div style={{ width: '100%', height: 300 }}>
                            {chartData.length > 0 ? (
                                <ResponsiveContainer>
                                    <BarChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} fontSize={10} />
                                        <YAxis axisLine={false} tickLine={false} fontSize={10} />
                                        <Tooltip cursor={{ fill: '#f8f9fa' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                        <Legend verticalAlign="top" align="right" iconType="circle" />
                                        <Bar name="Net Amount" dataKey="net" fill="#e9ecef" radius={[4, 4, 0, 0]} barSize={30} />
                                        <Bar name="Paid Amount" dataKey="paid" fill="#0d6efd" radius={[4, 4, 0, 0]} barSize={30} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-100 d-flex align-items-center justify-content-center text-muted italic">Insufficient data for trend analysis</div>
                            )}
                        </div>
                    </CardBody>
                </Card>

                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="bg-white p-4 border-bottom">
                        <Form onSubmit={handleSearchSubmit}>
                            <Row className="g-3 align-items-end">
                                <Col xs={12}>
                                    <Label className="x-small fw-bold text-muted text-uppercase">Quick Search</Label>
                                    <div className="input-group input-group-merge shadow-none border rounded-3 overflow-hidden">
                                        <span className="input-group-text bg-white border-0"><i className="bx bx-search text-muted"></i></span>
                                        <Input
                                            className="border-0 ps-0"
                                            placeholder="Search by Client Name or Invoice Number..."
                                            value={filters.search}
                                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                        />
                                        <Button color="primary" type="submit" className="px-4 fw-bold">Search</Button>
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </div>

                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="bg-light">
                                <tr className="text-muted small text-uppercase ls-1">
                                    <th className="ps-4 py-3">Invoice & Client</th>
                                    <th className="text-center">Adjustments</th>
                                    <th className="text-center">Collection Status</th>
                                    <th className="text-end pe-4">Final Net</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reports.length > 0 ? (
                                    reports.map((item) => (
                                        <tr key={item?.invoice_id}>
                                            <td className="ps-4 py-3">
                                                <div className="fw-bold text-dark">{item?.client?.name}</div>
                                                <div className="d-flex align-items-center gap-2 mt-1">
                                                    {/* 🔥 Badge-la ippo unga dynamic invoice_no varum da machan */}
                                                    <Badge color="light" className="text-primary border shadow-sm" style={{ fontSize: '11px' }}>
                                                        <FiFileText size={12} className="me-1" />
                                                        {item?.invoice_no}
                                                    </Badge>
                                                    <small className="text-muted x-small">{dayjs(item?.created_at).format('DD MMM YYYY')}</small>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <div className="x-small text-muted mb-1">TDS: ₹{item?.adjustments?.tds?.toLocaleString()}</div>
                                                <div className="x-small text-muted">Credit: ₹{item?.adjustments?.credit_note?.toLocaleString()}</div>
                                            </td>
                                            <td className="text-center" style={{ minWidth: '180px' }}>
                                                <div className="d-flex justify-content-between x-small mb-1">
                                                    <span className={`fw-bold text-${item?.financials?.status === 'paid' ? 'success' : 'warning'}`}>
                                                        {item?.financials?.status?.toUpperCase()}
                                                    </span>
                                                    <span className="text-muted">{item?.financials?.paid_percentage}%</span>
                                                </div>
                                                <Progress value={item?.financials?.paid_percentage} size="xs" color={item?.financials?.paid_percentage === 100 ? 'success' : 'primary'} className="rounded-pill" style={{ height: '6px' }} />
                                                <div className="x-small text-muted mt-1 fw-bold">Bal: ₹{item?.financials?.balance?.toLocaleString()}</div>
                                            </td>
                                            <td className="text-end pe-4">
                                                <div className="fw-bold text-dark fs-6">₹{item?.financials?.net_amount?.toLocaleString()}</div>
                                                <small className="text-muted x-small">Original: ₹{item?.pricing?.total_amount?.toLocaleString()}</small>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="4" className="py-5 text-center text-muted italic">No invoice records found.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {pagination?.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <Pagination className="pagination-rounded shadow-sm">
                            <PaginationItem disabled={filters?.page <= 1}>
                                <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters?.page - 1 })} />
                            </PaginationItem>
                            {[...Array(pagination?.total_pages || 0)].map((_, i) => (
                                <PaginationItem active={i + 1 === filters?.page} key={i}>
                                    <PaginationLink onClick={() => setFilters({ ...filters, page: i + 1 })}>{i + 1}</PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={filters?.page >= pagination?.total_pages}>
                                <PaginationLink next onClick={() => setFilters({ ...filters, page: filters?.page + 1 })} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
        </LocalizationProvider>
    );
};

const StatCard = ({ title, value, icon, color, bg }) => (
    <Col md={4}>
        <Card className="border-0 shadow-sm rounded-4 h-100 bg-white">
            <CardBody className="d-flex align-items-center gap-3 p-4">
                <div style={{ width: '55px', height: '55px', borderRadius: '16px', backgroundColor: bg, color: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
                    {icon}
                </div>
                <div>
                    <p className="m-0 text-muted x-small fw-bold text-uppercase ls-1">{title}</p>
                    <h3 className="m-0 fw-bold" style={{ color: '#2c3e50' }}>{value}</h3>
                </div>
            </CardBody>
        </Card>
    </Col>
);

export default InvoiceReportList;